import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useI18n } from 'hooks/useI18n';
import { Location } from 'model';
import { useRouter } from 'next/router';
import { forwardRef, useState } from 'react';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { components } from 'react-select';
import { config } from 'utils/config';
import { DEFAULT_LOCALE } from '../../services/multilingual.service';
import {
  convertListItemToValue,
  convertValueToListItem
} from './InputPlacesAutocomplete.adapters';

export type InputPlacesAutocompleteProps = {
  value?: Location | Location[] | null;
  onChange: (place: Location | Location[] | null) => void;
  placesTypes?: string[];
  placeholder?: string;
  noOptionsMessage?: string;
  isMulti?: boolean;
  isSmall?: boolean;
  autocompleteTypes?: string[];
};

const InputPlacesAutocomplete = forwardRef<
  HTMLInputElement,
  InputPlacesAutocompleteProps
>(
  (
    {
      autocompleteTypes = [
        'country',
        'administrative_area_level_2',
        'administrative_area_level_1',
        'locality'
      ],
      isMulti = false,
      isSmall = false,
      noOptionsMessage = undefined,
      onChange = () => {
        // NO-op
      },
      placeholder,
      value
    },
    ref
  ) => {
    const { locale } = useRouter();
    const { t } = useI18n('common');

    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const ClearIndicator = (props: any): React.ReactElement => {
      return (
        <components.ClearIndicator {...props}>
          <FontAwesomeIcon icon={faTimes} />
        </components.ClearIndicator>
      );
    };

    const handleOnInputChange = (newValue: string): void => {
      setMenuIsOpen(newValue.length > 0);
    };

    return (
      <>
        <GooglePlacesAutocomplete
          apiKey={config.googlePlacesApiKey}
          autocompletionRequest={{
            types: autocompleteTypes
          }}
          apiOptions={{
            language: locale
          }}
          selectProps={{
            value: convertValueToListItem(locale || DEFAULT_LOCALE, value),
            onChange: (place, action) => {
              if (action.action === 'remove-value' && Array.isArray(value)) {
                onChange(
                  value.filter(
                    (v) => v?.placeId !== action.removedValue.value.placeId
                  )
                );
              } else {
                onChange(convertListItemToValue(place));
              }
            },
            ref: ref,
            onInputChange: handleOnInputChange,
            menuPortalTarget:
              typeof window !== 'undefined'
                ? document.querySelector('body')
                : undefined,
            classNamePrefix: 'mf-select',
            className: classNames({
              'mf-select': true,
              'mf-select-autocomplete': true,
              'cs-sm': isSmall
            }),
            isClearable: true,
            placeholder,
            noOptionsMessage: () =>
              noOptionsMessage || t('inputPlacesAutocomplete.noResults'),
            loadingMessage: () => t('inputPlacesAutocomplete.loading'),
            menuIsOpen,
            // @ts-expect-error it accepts boolean, indeed. Not only "false"
            isMulti,
            components: {
              ClearIndicator,
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null
            }
          }}
        />
      </>
    );
  }
);

InputPlacesAutocomplete.displayName = 'InputPlacesAutocomplete';

export default InputPlacesAutocomplete;
